import L from 'leaflet';
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useMap } from 'react-leaflet';
import CustomLeafletControl from '../CustomLeafletControl';
import {
  COMPACT_MAP_HEIGHT,
  COMPACT_MAP_HORIZONTAL_CLEARANCE,
  COMPACT_MAP_WIDTH,
  MAP_HORIZONTAL_CLEARANCE,
  MAP_VERTICAL_CLEARANCE,
} from './config/mapToolsPanel.config';
import { getMapSize } from './utils/mapToolsPanel.utils';

/**
 * Places a reusable tools panel inside a Leaflet map. The panel stays within the measured map size,
 * scrolls internally when the map is short, and can collapse automatically on compact maps.
 *
 * @param {Object} props
 * @param {string} [props.position] Leaflet corner where the panel is placed.
 * @param {number} [props.width] Preferred expanded width in pixels.
 * @param {string} [props.header] Optional visible panel title.
 * @param {boolean} [props.collapsible] Allows compact and manual collapsed states.
 * @param {*} props.children Panel controls and content.
 * @returns {JSX.Element}
 */
const MapToolsPanel = ({ position = 'topright', width, header, collapsible = false, children }) => {
  const map = useMap(); // Leaflet map instance
  const headerId = useId();
  const contentId = useId();
  const [mapSize, setMapSize] = useState(() => getMapSize(map));
  const isCompactWidth = mapSize.width !== null && mapSize.width <= COMPACT_MAP_WIDTH;
  const isCompactHeight = mapSize.height !== null && mapSize.height <= COMPACT_MAP_HEIGHT;
  const isCompact = collapsible && (isCompactWidth || isCompactHeight);
  const [isCollapsed, setIsCollapsed] = useState(isCompact);
  const previousCompactState = useRef(isCompact);

  // Clicking or scrolling panel controls must does not trigger map interactions like placing a shape, panning, or zooming.
  const setPanelRef = useCallback((node) => {
    if (!node) return;

    L.DomEvent.disableClickPropagation(node);
    L.DomEvent.disableScrollPropagation(node);
  }, []);

  const updateMapSize = useCallback(() => {
    const nextMapSize = getMapSize(map);

    // Reuse the current object when nothing changed to avoid an unnecessary React update.
    setMapSize((currentMapSize) =>
      currentMapSize.width === nextMapSize.width && currentMapSize.height === nextMapSize.height
        ? currentMapSize
        : nextMapSize,
    );
  }, [map]);

  // Adds and removes map resize listeners and calls updateMapSize
  useEffect(() => {
    updateMapSize();
    map?.on?.('resize', updateMapSize);

    return () => map?.off?.('resize', updateMapSize); // Removes the resize listener when the component is unmounted
  }, [map, updateMapSize]);

  useEffect(() => {
    if (!collapsible) {
      setIsCollapsed(false);
      previousCompactState.current = false;
      return;
    }

    // Sets the collapsed state to isCompact when map either enters compact size or exits it
    if (previousCompactState.current !== isCompact) {
      setIsCollapsed(isCompact);
      previousCompactState.current = isCompact;
    }
  }, [collapsible, isCompact]);

  const horizontalClearance = isCompactWidth
    ? COMPACT_MAP_HORIZONTAL_CLEARANCE // How much space on map left unused by tools panel when compact
    : MAP_HORIZONTAL_CLEARANCE; // How much space on map left unused by tools panel when not compact

  // Always leave part of the map visible beside the panel, including on 320-pixel screens.
  const maximumPanelWidth = mapSize.width
    ? Math.max(mapSize.width - horizontalClearance, 120)
    : undefined;

  const expandedWidth = width ? Math.min(width, maximumPanelWidth ?? width) : undefined;

  // Leave room for map controls and make the panel body scroll when content is taller. Ensures theres always visible map underneath panel.
  const maximumPanelHeight = mapSize.height
    ? Math.max(mapSize.height - MAP_VERTICAL_CLEARANCE, 120)
    : undefined;

  return (
    <CustomLeafletControl position={position}>
      <section
        ref={setPanelRef}
        style={{
          width: isCollapsed ? 'auto' : expandedWidth,
          minWidth: isCollapsed ? 0 : Math.min(170, expandedWidth ?? 170),
          maxWidth: maximumPanelWidth,
          maxHeight: maximumPanelHeight,
        }}
        className={`map-tools-panel ${isCollapsed ? 'is-collapsed' : ''}`}
        aria-labelledby={header ? headerId : undefined}
      >
        {header &&
          (collapsible ? (
            <button
              type="button"
              className="map-tools-header map-tools-panel-toggle"
              aria-expanded={!isCollapsed}
              aria-controls={contentId}
              aria-label={`${isCollapsed ? 'Expand' : 'Collapse'} ${header}`}
              onClick={() => setIsCollapsed((currentValue) => !currentValue)}
            >
              <span id={headerId}>{header}</span>
            </button>
          ) : (
            <h6 id={headerId} className="map-tools-header">
              {header}
            </h6>
          ))}

        <div id={contentId} className="map-tools-panel-content" hidden={isCollapsed}>
          {children}
        </div>
      </section>
    </CustomLeafletControl>
  );
};

export default MapToolsPanel;
